<template>
  <div class="mb-5" :key="shipment._id || 1">
    <ul class="list-group list-group-flush modal-body-fixed-footer" data-title="Shipment Form">
      <li class="list-group-item py-5" v-if="allPoList.length">
        <h5>Purchase Orders</h5>
        <autocomplete-input
          placeholder="Select Po(s)..."
          :optionsV="allPoListOptions"
          :multiple="true"
          :isObject="true"
          :outValKey="true"
          label="friendlyPurchaseOrderDetailId"
          v-model="shipment.poIds"
          :track-by="'friendlyPurchaseOrderDetailId'"
        >
          <template slot="option" slot-scope="props">
            <div>
              <div class="d-flex align-items-center">
                <span>{{ props.option.friendlyPurchaseOrderDetailId }}</span>
                <span v-if="props.option?.supplierName" class="ml-2">({{ props.option?.supplierName }})</span>
                <span v-if="props.option?.shipmentId" class="badge badge-success ml-2">{{
                  props.option.shipmentId
                }}</span>
              </div>
            </div>
          </template>
        </autocomplete-input>
      </li>
      <li class="list-group-item py-5" v-if="shipment.fobPoint">
        <h5>From</h5>
        <div class="row">
          <div
            class="col-md-8"
            v-if="!shipment.fobPoint.formVisible && ((purchaseOrder && purchaseOrder.supplier) || allPoList.length)"
          >
            <autocomplete-input
              :path="'/fobPoints?fields=city&fields=state&fields=zip'"
              name="FobPoint"
              loadOnFocus
              isObject
              track-by="_id"
              v-model="shipment.fobPointId"
              :query="autocompleteQuery"
              :internalSearch="false"
            >
              <template slot="option" slot-scope="props">
                {{
                  [props.option.city, props.option.state, props.option.zip].filter((e) => e).join(', ') ||
                  props.option.fobId ||
                  ''
                }}
                <span class="badge badge-info">{{ props.option.fobId || 'NA' }}</span>
              </template>
              <template slot="singleLabel" slot-scope="props">
                {{
                  [props.option.city, props.option.state, props.option.zip].filter((e) => e).join(', ') ||
                  props.option.fobId ||
                  ''
                }}
                <span class="badge badge-info">{{ props.option.fobId || 'NA' }}</span>
              </template>
            </autocomplete-input>
          </div>
          <div class="col-md-4">
            <validated-input
              label="Quantity"
              v-model="shipment.Quantity.value"
              :validate="{ required: shippingRequired, min_value: shippingRequired ? 1 : 0 }"
              mode="instant"
            />
          </div>
        </div>
        <a @click="toggleFobFormVisibility()">Add Fob Point</a>
        <b-collapse id="collapse-1" class="mt-2" v-model="shipment.fobPoint.formVisible">
          <label>Fob Id</label>
          <b-form-input v-model="shipment.fobPoint.fobId" name="fobId" />
          <address-fields v-model="shipment.fobPoint" :hide="['street2']" />
        </b-collapse>
      </li>

      <li class="list-group-item py-5">
        <h5>To</h5>
        <div>
          <div class="">
            <div class="mt-3 card p-3">
              <b-form-group class="">
                <b-form-radio-group v-model="shipment.access" class="m-0 d-flex justify-content-between">
                  <b-form-radio value="Customer">
                    Customer
                    <i
                      class="fas fa-question-circle ml-1 text-light"
                      v-b-popover.hover
                      :title="`Shipment is to customer.`"
                    ></i>
                  </b-form-radio>
                  <b-form-radio value="Decorator">
                    Decorator
                    <i
                      class="fas fa-question-circle ml-1 text-light"
                      v-b-popover.hover
                      :title="`Shipment is to a decorator.`"
                    ></i>
                  </b-form-radio>
                  <b-form-radio value="Distributor">
                    Distributor
                    <i
                      class="fas fa-question-circle ml-1 text-light"
                      v-b-popover.hover
                      :title="`Shipment is to a distributor.  Shipment updates will not be shown to customer.`"
                    ></i>
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
              <autocomplete-input
                :key="shipment.access"
                :path="(shipment.access === 'Decorator' && '/distributors/-1/supplier-decorators/addresses') || ''"
                loadOnFocus
                isObject
                track-by="_id"
                :internal-search="false"
                :only-search="true"
                :filterOptions="(shipment.access === 'Customer' && filterCustomerAddresses) || null"
                v-model="selectedAddress"
                :options-v="addressOptions"
                @select="selectAddress"
                placeholder="Saved addresses..."
              >
                <template slot="option" slot-scope="props">
                  <div class="">
                    <h6 v-if="props.option.description">{{ props.option.description }}</h6>
                    <AddressFormat :address="props.option" :list="true" class="small"></AddressFormat>
                  </div>
                </template>
                <template slot="singleLabel" slot-scope="props">
                  <div class="pt-0 pb-0" v-if="props.option">
                    <h6 v-if="props.option.description">{{ props.option.description }}</h6>
                    <AddressFormat :address="props.option" :list="true" class="small"></AddressFormat>
                  </div>
                </template>
              </autocomplete-input>
            </div>
          </div>
          <div id="collapseShippingAddress" class="my-3">
            <div class="row">
              <div class="col-md-6">
                <div class="d-flex mb-3 flex-column">
                  <validated-input 
                   label="Contact"
                   v-model="shipment.contactName" 
                   :validate="{ required: true }"
                   />
                  <a href="javascript:;" class="small" style="margin-top: -18px" @click.prevent="addContact"
                    >Auto populate</a
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex mb-3 flex-column">
                  <validated-input 
                   label="Company"
                   v-model="shipment.companyName" 
                   />
                  <a href="javascript:;"
                    class="small"
                    style="margin-top: -18px"
                    @click.prevent="addCompany"
                    >Auto populate</a
                  >
                </div>
              </div>
            </div>
            <address-fields
              v-model="shipment"
              :is-city-required="shippingRequired"
              :is-state-required="stateRequired"
              :is-country-required="shippingRequired"
              :is-zip-required="shippingRequired"
              :hide="['addressName']"
            />
            <b-form-checkbox v-if="shipment.access !== 'Decorator'" v-model="shipment.saveAddress" class="my-1"
              >Save Address</b-form-checkbox
            >
          </div>
        </div>
      </li>
      <li class="list-group-item py-5" v-if="shipment.shipReferences">
        <h5>References Fields</h5>
        <validated-input
          label="Field 1"
          v-model="shipment.shipReferences.referenceField1"
          :validate="{ required: false }"
        />
        <validated-input
          label="Field 2"
          v-model="shipment.shipReferences.referenceField2"
          :validate="{ required: false }"
        />
      </li>
      <li class="list-group-item py-5">
        <h5>Packages</h5>
        <template>
          <div class="text-danger py-3" v-if="tryCategoryShipment">
            <i class="far fa-exclamation-triangle mr-2"></i>
            Shipping dimensions provided from supplier might be incorrect. If they seem off we recommend
            <a @click="useCategoryDimension" :disabled="loadingDimension"> using category part dimensions </a> which
            averages dimensions across all products associated with this products category.
          </div>
          <div
            class="mb-3"
            v-if="!shipment.width || !shipment.height || !shipment.depth || !shipment.weight || !shipment.numberOfPackages"
          >
            <a @click="useCategoryDimension" :disabled="loadingDimension"> Use category shipping dimensions </a>
          </div>
          <div class="row d-flex align-items-center">
            <div class="col-4" v-for="(field, index) in dimensionFields" :key="index">
              <validated-input
                type="number"
                :label="field.label"
                :value="shipment[field.name]"
                @input="val => updateDimensionField(field.name, val)"
                :validate="{ min_value: shippingRequired ? field.minValue : 0, required: shippingRequired }"
                :placeholder="field.placeholder"
              />
            </div>
            <div class="col-4 text-right">
              <button
                :key="watchProps"
                @click="loadRates"
                class="btn btn-secondary btn-block mt-2"
                :disabled="!loaded || !!error"
                v-b-popover.hover="{
                  content: `Calculate shipping`,
                  html: true
                }"
              >
                <i class="fas fa-calculator"></i>
              </button>
            </div>
          </div>
          <div class="form-group">
            <div class="text-center" v-if="!loaded">
              <i class="far fa-spinner-third fa-spin fa-2x text-primary"></i>
            </div>
            <div class="invalid-feedback d-block pt-2" v-else-if="error">
              <b-alert variant="danger" show>{{ error }}</b-alert>
            </div>
            <ShippingRatesDropdown
              :key="labelUpdated"
              :productId="productId"
              :shipment="shipment"
              :fobPointId="(fobPointId || shipment.fobPointId || {})._id || fobPointId || shipment.fobPointId"
              :supplierCode="supplierCode"
              :purchaseOrder="purchaseOrder"
              :rates="rates"
              :value="rate"
              @input="chooseRate"
              @onSave="onSave"
              v-else
            />
          </div>
        </template>
      </li>
      <li class="list-group-item py-5">
        <h5>Carrier</h5>
        <ShipmentFormCarriers :shipment="shipment" :order="purchaseOrder.order" @select="select" />
        <div class="row">
          <div class="col-6">
            <ShipmentCarierInput v-model="shipment.carrier" :required="shippingRequired" />
          </div>
          <div class="col-6">
            <validated-input
              label="Service"
              v-model="shipment.carrierService"
              :validate="{ required: shippingRequired }"
            />
          </div>
          <div class="col-6">
            <validated-input label="Account Name" v-model="shipment.thirdPartyAccountName" />
          </div>
          <div class="col-6">
            <validated-input label="Account Number" v-model="shipment.thirdPartyAccountNumber" />
          </div>
          <div class="col-4" v-for="(field, index) in priceFields" :key="index">
            <div class="d-flex align-items-center">
              <validated-input
                :label="field.label"
                v-model="shipment[field.name]"
                :append="field.append"
                :validate="{ decimal: true, required: shippingRequired }"
                @input="e => updatePriceField(field.name, e)"
                @blur="e => field.onBlur(shipment, field.blurParams)"
              />
            </div>
          </div>
        </div>
        <div class="alert alert-danger text-wrap small" v-if="shipmentCarrierValidationError">
          {{ shipmentCarrierValidationError }}
        </div>
      </li>
      <li class="list-group-item py-5" v-if="!hidePackages">
        <div class="d-flex">
          <h5>Tracking</h5>
        </div>
        <TrackingPackageList
          :order="this.purchaseOrder?.order"
          :trackingDetails="shipment"
          :order-id="parseFloat($route.params.orderId)"
          :poIds="shipment.poIds"
          :shipment="shipment"
          :po-number="poNumber"
        />
        <EmailTracking
          :trackingDetails="trackingDetails"
          :customer="this.purchaseOrder?.order?.customerId"
          :order="this.purchaseOrder?.order"
          :poNumber="poNumber"
        />
      </li>
      <li class="list-group-item py-5">
        <div class="d-flex align-items-center mb-2">
          <h5>Shipment Documents</h5>
          <i
            class="fas fa-question-circle text-light ml-2"
            v-b-popover.hover="{
              content: 'You need to save the Purchase Order with shipment before uploading shipment documents.'
            }"
          >
          </i>
        </div>

        <OrderDocuments
          :type="ORDER_DOCUMENT_TYPES.SHIPMENT_LABEL"
          :params="{ orderId: $route.params.orderId }"
          :documents="shipment.Documents"
          @successOrderUpload="successOrderUpload"
          @deleteOrderDocument="deleteOrderDocument"
        />
      </li>
      <li class="list-group-item" v-if="showAccounting">
        <accounting-dropdowns
          :value="shipment"
          :qbAccounts="qbAccounts"
          :poInvoices="purchaseOrder.invoices"
        ></accounting-dropdowns>
      </li>
    </ul>
    <div class="card-footer modal-fixed-footer p-3" v-if="!hidePackages">
      <b-button variant="primary" @click="onSubmit" :disabled="loading || errors.any()" class="m-1"
        >Save Shipment <i class="far fa-spinner-third fa-spin" v-if="loading"></i>
      </b-button>
    </div>
  </div>
</template>
<script>
import priceMarginMixin from '../../../../mixins/priceMarginMixin';
import ShippingRatesDropdown from '../../../../components/ShippingRatesDropdown';
import AddressFormat from '../../../../components/AddressFormat';
import { mapGetters } from 'vuex';
import PhoneField from '@mtntop/utils/src/components/PhoneField';
import DropdownButton from '../../../../components/DropdownButton';
import mPowerCardMixin from '../../../../components/MPowerCards/mixin';
import HTTP from '../../../../utils/http-common';
import { ORDER_DOCUMENT_TYPES } from '../../../../constants';

export default {
  inject: ['$validator'],
  name: 'ShipmentForm',
  mixins: [priceMarginMixin, mPowerCardMixin],
  data() {
    return {
      ORDER_DOCUMENT_TYPES,
      totalShipmentCosts: 0,
      shipmentTypeIndex: 1,
      labelUpdated: 1,
      selectedAddress: null,
      tryCategoryShipment: false,
      showSaveCustomerAddress: true,
      error: '',
      loaded: true,
      loadingDimension: false,
      loading: false,
      fobPointId: false,
      rate: {},
      rates: [],
      dimensionFields: [
        { name: 'width', label: 'Width', minValue: 0.01, placeholder: 'in' },
        { name: 'height', label: 'Height', minValue: 0.01, placeholder: 'in' },
        { name: 'depth', label: 'Depth', minValue: 0.01, placeholder: 'in' },
        { name: 'weight', label: 'Weight', minValue: 0.01, placeholder: 'lbs' },
        { name: 'numberOfPackages', label: 'Boxes', minValue: 1, placeholder: '' }
      ],
      priceFields:  [
        { name: 'shipmentCost', label: 'Cost', append: '$', onBlur: this.priceChanged, blurParams: ['margin', 'shipmentCost', 'shipmentPrice'] },
        { name: 'margin', label: 'Margin', append: '%', onBlur: this.marginChanged, blurParams: ['shipmentPrice', 'shipmentCost', 'margin'] },
        { name: 'shipmentPrice', label: 'Price', append: '$', onBlur: this.priceChanged, blurParams: ['margin', 'shipmentCost', 'shipmentPrice'] }
      ]
    };
  },
  components: {
    DropdownButton,
    PhoneField,
    AddressFormat,
    ShippingRatesDropdown,
    TrackingPackageList: () => import('./Shipment/TrackingPackageList'),
    OrderDocuments: () => import('./OrderDocuments'),
    FobPointForm: () => import('@/portals/Administration/Suppliers/FobPoints/FobPointForm'),
    ShipmentFormCarriers: () => import('./ShipmentFormCarriers'),
    ShipmentCarierInput: () => import('@/components/ShipmentCarierInput'),
    EmailTracking: () => import('./SendTracking/EmailTracking.vue')
  },
  props: {
    onDone: Function,
    hidePackages: Boolean,
    productId: {
      type: String
    },
    partId: {
      type: String
    },
    supplierCode: {
      type: String
    },
    shipment: {
      type: Object,
      required: true
    },
    qbAccounts: {
      type: Array | Object,
      default: () => []
    },
    allPoList: {
      type: Array | Object,
      default: () => []
    },
    showAccounting: {
      type: Boolean,
      default: false
    },
    fobPoints: {
      type: Array,
      required: true
    },
    purchaseOrder: {
      type: Object,
      required: true
    },
    customerContact: Object
  },
  methods: {
    async successOrderUpload(documents) {
      const bucketToSave = `/orders/${this.$route.params.orderId}/shipments`;
      const body = {
        url: documents[documents.length - 1].filePath,
        parentName: 'orders',
        bucketToSave,
        fileType: 'Shipment label'
      };
      await HTTP.post(
        `/orders/${this.$route.params.orderId}/purchase-orders/${this.purchaseOrder._id}/shipments/${
          this.shipment._id
        }/documents/${documents[documents.length - 1]._id}`,
        body
      );
      this.shipment.Documents.splice(0, 0, documents[0]);
    },
    async deleteOrderDocument(documentId) {
      await HTTP.delete(
        `/orders/${this.$route.params.orderId}/purchase-orders/${this.purchaseOrder._id}/shipments/${this.shipment._id}/documents/${documentId}`
      );
      this.shipment.Documents.splice(
        this.shipment.Documents.findIndex((document) => document._id === documentId),
        1
      );
      this.notificateSuccess('Document deleted');
    },
    async onSubmit($e) {
      if (this.allPoList.length && !this.shipment.poIds?.length) {
        return this.notifyError('Please select at least one PO');
      }
      try {
        this.loading = true;
        await this.onSubmitDone($e);
      } catch (e) {
        console.log('onSubmit shipment form:', e);
      } finally {
        this.loading = false;
      }
    },
    filterCustomerAddresses(options, search) {
      const res = options.filter((option) =>
        ['zip', 'country', 'street', 'city', 'companyName', 'contactName', 'state', 'street1'].find((key) =>
          (option[key] || '').toLowerCase().match(search)
        )
      );
      return res;
    },
    select(option) {
      Object.assign(this.shipment, option);
      this.$forceUpdate();
    },
    addContact() {
      this.shipment.contactName = this.customerContact.fullName;
    },
    addCompany() {
      this.shipment.companyName = this.customerContact.companyName;
    },
    useCategoryDimension(e) {
      e.preventDefault();
      this.loadShippingPackages(true, true);
    },
    async loadShippingPackages(forceUpdate = false, forceUseCategoryDimension = false) {
      try {
        this.loadingDimension = true;
        const shippingPackage = (
          await this.$http.post(`/shipments/packages/parts`, {
            forceUseCategoryDimension,
            store: this.purchaseOrder.order.storeId?._id,
            order: this.purchaseOrder.order._id,
            poId: this.purchaseOrder.parentPoId,
            supplierCode: this.supplierCode,
            productId: this.productId,
            partId: this.partId,
            quantity: this.shipment.Quantity.value,
            mustHavePart: !this.manual
          })
        ).data;
        if (shippingPackage.length && (!this.shipment.numberOfPackages || forceUpdate)) {
          const { dimensions, weight, numberOfPackages } = shippingPackage[0];
          this.$set(this.shipment, 'height', dimensions[0]);
          this.$set(this.shipment, 'width', dimensions[1]);
          this.$set(this.shipment, 'depth', dimensions[2]);
          this.$set(this.shipment, 'weight', weight);
          this.$set(this.shipment, 'numberOfPackages', numberOfPackages);
          //this.loadRates();
          this.recalculateSaveShipment();
        }
      } catch (e) {
        console.log(e);
        this.shipment.height = 0;
        this.shipment.width = 0;
        this.shipment.depth = 0;
        this.shipment.weight = 0;
        this.shipment.numberOfPackages = 0;
      } finally {
        this.loadingDimension = false;
      }
    },
    recalculateSaveShipment() {
      try {
        if (!this.purchaseOrder.order.customerId?.CustomerAddresses) return;
        for (let address of this.purchaseOrder.order.customerId.CustomerAddresses) {
          if (
            this.shipment.street1 === address.street1 &&
            this.shipment.street2 === address.street2 &&
            this.shipment.city === address.city &&
            this.shipment.state === address.state &&
            this.shipment.zip === address.zip &&
            this.shipment.country === address.country
          ) {
            this.showSaveCustomerAddress = false;
            return;
          }
        }
        this.showSaveCustomerAddress = true;
      } catch (e) {
        console.log(e);
      }
    },
    selectAddress(address) {
      this.$nextTick(() => {
        this.selectedAddress = address;
        this.$set(this.shipment, 'phone', address.phone);
        if (address.contactName && address.contactName !== this.shipment.contactName) {
          this.$set(this.shipment, 'contactName', address.contactName);
        }
        if (address.companyName && address.companyName !== this.shipment.companyName) {
          this.$set(this.shipment, 'companyName', address.companyName);
        }
        this.$set(this.shipment, 'street1', address.street1);
        this.$set(this.shipment, 'street2', address.street2);
        this.$set(this.shipment, 'city', address.city);
        this.$set(this.shipment, 'state', address.state);
        this.$set(this.shipment, 'zip', address.zip);
        this.$set(this.shipment, 'country', address.country || '');
        this.showSaveCustomerAddress = false;
        if (this.shipment.country?.toUpperCase() === 'USA') {
          this.$set(this.shipment, 'country', 'US');
        }
      });
    },
    onSave(labels) {
      if (!this.shipment.labels) this.shipment.labels = [];
      this.shipment.labels = labels;
      this.labelUpdated = Date.now();
    },
    chooseRate(rate) {
      this.rate = rate;
      this.shipment.carrier = rate.carrier;
      this.shipment.margin = rate.shippingMargin;
      this.shipment.carrierService = rate.serviceName;
      this.$set(this.shipment, 'shipmentPrice', _.round(rate.price / 100, 2));
      this.$set(this.shipment, 'shipmentCost', _.round((rate.shippingCost || rate.price) / 100, 2));
      if (typeof this.shipment.margin !== 'undefined')
        this.marginChanged(this.shipment, ['shipmentPrice', 'shipmentCost', 'margin'], 2);
      this.shipment.thirdPartyAccountName = rate.accountName;
      this.shipment.thirdPartyAccountNumber = rate.accountNumber;
      this.formatFloats(this.shipment, ['shipmentPrice', 'shipmentCost']);
    },
    updateDimensionField: _.debounce(function(fieldName, value) {
      this.$set(this.shipment, fieldName, +value);
      this.error = '';
      this.shipmentValidationError();
      this.recalculateSaveShipment();
    }, 300),
    updatePriceField: _.debounce(function(fieldName, value) {
      this.$set(this.shipment, fieldName, value);
    }, 300),
    async loadRates() {
      if (this.shipmentValidationError()) return;
      if (!this.isValidFobPoint) return;
      if (!this.loaded) return;
      this.loaded = false;
      this.error = '';
      try {
        let response;
        this.rates = [];

        const estimate = {
          ...(this.purchaseOrder.order?._id && {
            order: {
              orderId: this.purchaseOrder.order.orderId,
              _id: this.purchaseOrder.order._id
            }
          }),
          quantity: this.shipment.Quantity ? this.shipment.Quantity.value : null,
          supplierCode: this.supplierCode,
          productId: this.productId,
          product: {
            productName:
              (this.purchaseOrder?.LineItemArray?.length && this.purchaseOrder?.LineItemArray[0].productName) ||
              this.productId,
            slug:
              (this.purchaseOrder?.LineItemArray?.length && this.purchaseOrder?.LineItemArray[0].localProduct?.slug) ||
              ''
          },
          destination: this.shipment
        };
        const product =
          this.purchaseOrder?.LineItemArray?.find((e) => e.productId === this.productId) ||
          this.purchaseOrder.order?.PurchaseOrders.map((e) =>
            e.LineItemArray?.find((e) => e.productId === this.productId)
          ).find((e) => e);
        if (product?.localProduct?._id || product?.localProduct) {
          estimate.productHashId = product.localProduct?._id || product.localProduct;
        }
        if (!this.shipment.fobPointId) {
          estimate.fobPointData = this.fobPoint;
        } else {
          estimate.fobPointId = (this.shipment.fobPointId || {})._id || this.shipment.fobPointId;
        }
        if (this.shipment.numberOfPackages) {
          response = (
            await this.http.post('/shipments/estimate', {
              ...estimate,
              storeId: this.store || this.purchaseOrder?.order?.storeId?._id,
              partId: this.partId,
              packages: Array(this.shipment.numberOfPackages).fill({ ...this.shipment, quantity: 1 }),
              tryCategoryShipment: this.tryCategoryShipment
            })
          ).data;
        } else {
          response = (
            await this.http.post('/shipments/estimate', {
              ...estimate,
              partId: this.partId,
              storeId: this.store || this.purchaseOrder?.order?.storeId?._id,
              tryCategoryShipment: this.tryCategoryShipment
            })
          ).data;
        }
        // console.log(response);
        if (response.error) {
          this.error = response.error;
        } else if (response) {
          this.rates = response;
          this.tryCategoryShipment = this.rates.filter((e) => e.usedCategoryShipping).length > 0;
        }
      } catch (e) {
        this.error = 'Error happened (check shipment addresses or dimensions)';
      }
      this.recalculateSaveShipment();
      this.loaded = true;
    },
    toggleFobFormVisibility() {
      this.shipment.fobPoint.formVisible = !this.shipment.fobPoint.formVisible;
      this.$set(this.shipment, 'fobPointId', null);
    },
    setError(message) {
      this.error = message;
      return this.error;
    },
    shipmentValidationError() {
      if (!this.shipment.width || !this.shipment.height || !this.shipment.depth || !this.shipment.weight || !this.shipment.numberOfPackages) {
        return this.setError('All package dimensions and weight must be greater than 0');
      }
      this.error = '';
      if (!this.shipment.zip || !this.shipment.country || !this.shipment.state) {
        return this.setError('No destination selected');
      }
      if (!this.shipment.contactName) {
        return this.setError('Shipping contact required');
      }
      if (this.manual && !this.isPackageValid) {
        return this.setError('No package info provided');
      }
      if (Number(this.shipment.Quantity.value) < 1) {
        return this.setError('Quantity should be 1 or more');
      }
      if (!this.isValidFobPoint) {
        return this.setError('No FOB point selected');
      }
      if (this.error && this.error.includes('intermediate')) {
        return this.setError('Please verify country, city, state, or postal code');
      }
      return false;
    }
  },
  computed: {
    trackingDetails() {
      const shipment = this.shipment;
      const purchaseOrder = shipment?.poIds
        ? this.purchaseOrder.order?.PurchaseOrders?.find((po) =>
            shipment?.poIds.includes(po.friendlyPurchaseOrderDetailId)
          )
        : null;
      if (purchaseOrder?.purchaseOrderEmails?.length) {
        shipment.lastTrackingEmail = purchaseOrder?.purchaseOrderEmails[0];
      }
      return shipment;
    },
    addressOptions() {
      if (this.shipment.access === 'Decorator') return [];
      if (this.shipment.access === 'Distributor') {
        return this.purchaseOrder.order.distributorId?.DistributorAddresses || [];
      }
      return [
        ...(this.purchaseOrder.order.customerId?.CustomerAddresses || []),
        ...(this.purchaseOrder.order.customerId?.companyId?.companyAddresses || []),
        ...(this.purchaseOrder.order.customerId?.companyId?.companyBillingAddresses || [])
      ].filter((e) => e);
    },
    autocompleteQuery() {
      const fobPoints = this.purchaseOrder?.LineItemArray?.[0]?.localProduct?.fobPoints || [];
      const productFobPoints = this.purchaseOrder?.LineItemArray?.[0]?._product?.fobPoints || [];
      const supplierArrayOfFobPoints = this.purchaseOrder?.supplier?.fobPoints?.map((fp) => fp._id) || [];
      const createdOrderFobPoints = this.purchaseOrder?.fobPoints?.map((fp) => fp._id || fp) || [];
      const supplierPsCode = this.purchaseOrder?.supplierCode || null;

      let prioritizedFobPoints = fobPoints;
      if (!fobPoints.length) prioritizedFobPoints = productFobPoints;
      if (!productFobPoints.length) prioritizedFobPoints = supplierArrayOfFobPoints;
      if (!supplierArrayOfFobPoints.length) prioritizedFobPoints = createdOrderFobPoints;
      if (!createdOrderFobPoints.length) {
        return { supplierCode: supplierPsCode };
      }
      return { _id: { $in: prioritizedFobPoints } };
    },
    poNumber() {
      return (
        this.$route.params.purchaseOrderId ||
        this.purchaseOrder.friendlyPurchaseOrderDetailId ||
        (this.shipment.poIds?.length && this.shipment.poIds[0])
      );
    },
    allPoListOptions() {
      const options = this.allPoList.map((e) => ({
        supplierName: e.supplier?.name,
        friendlyPurchaseOrderDetailId: e.friendlyPurchaseOrderDetailId
      }));
      const otherShipments = this.purchaseOrder?.order?.ShipmentArray?.filter(
        (s) => s.shipmentId !== this.shipment.shipmentId
      );
      if (otherShipments.length) {
        options.forEach((option) => {
          option.shipmentId = otherShipments.find((s) =>
            s.poIds.includes(option.friendlyPurchaseOrderDetailId)
          )?.shipmentId;
        });
      }
      return options;
    },
    suppliersCode() {
      return (this.allPoList.length && this.allPoList.map((e) => e.supplier?.psCode).filter((e) => e)) || [];
    },
    fobPoint() {
      return this.shipment.fobPointId || this.shipment.fobPoint || {};
    },
    isValidFobPoint() {
      return this.fobPoint && (this.fobPoint.zip || this.fobPoint._id);
    },
    watchPackageInfo() {
      return [this.shipment.Quantity.value, this.partId].join();
    },
    ...mapGetters(['loggedInUser']),
    stateRequired() {
      return ['US', 'CA', 'AU', 'BR'].includes(this.shipment.country);
    },
    shippingRequired() {
      return false;
    },
    watchProps() {
      const props = [
        'city',
        'state',
        'zip',
        'country',
        'fobPointId',
        'numberOfPackages',
        'weight',
        'depth',
        'width',
        'height'
      ].map((f) => this.shipment[f]);
      return props.join();
    },
    shipmentCarrierValidationError() {
      let servicesError = ['carrier', 'carrierService', 'thirdPartyAccountName', 'thirdPartyAccountNumber'].filter(
        (i) => !this.shipment[i]
      );
      if (servicesError.length) {
        return `Missing ${servicesError.map((i) => _.startCase(i)).join(', ')}`;
      }
    },
    manual() {
      return this.purchaseOrder.type === 'Custom';
    },
    isPackageValid() {
      return this.shipment.numberOfPackages && this.shipment.weight && this.shipment.height && this.shipment.width && this.shipment.depth;
    }
  },
  watch: {
    watchPackageInfo: _.debounce(function () {
      this.loadShippingPackages(true);
    }, 1000),
  },
  async created() {
    try {
      if (!this.shipment.contactName && this.customerContact) this.shipment.contactName = this.customerContact.fullName;
      if (!this.shipment.companyName && this.customerContact)
        this.shipment.companyName = this.customerContact.companyName;
      if (!this.shipment.thirdPartyAccountName && this.loggedInUser.distributorId) {
        this.shipment.thirdPartyAccountName = this.loggedInUser.distributorId.name;
      }
      if (!this.shipment.fobPoint) {
        const fobPoint = {
          state: '',
          zip: '',
          street1: '',
          country: '',
          city: '',
          supplierCode: this.supplierCode,
          formVisible: false,
          manual: true,
          ...this.shipment.fobPointId
        };
        this.$set(this.shipment, 'fobPoint', fobPoint);
      }
      if (!this.shipment.access) this.$set(this.shipment, 'access', 'Customer');
      if (!this.shipment.shipReferences)
        this.shipment.shipReferences = { referenceField1: null, referenceField2: null };
      this.loadShippingPackages(true);
      this.formatFloats(this.shipment, ['shipmentPrice', 'shipmentCost']);
    } catch (e) {
      console.log(e);
    }
  }
};
</script>
